/**
 * Demo Data Registry
 *
 * A lightweight in-memory store for tour demo data.
 * Reducers check this registry before making API calls — if demo data
 * is registered for a given ID, it's returned directly (no backend call).
 *
 * Usage:
 *   // In tour inject thunk:
 *   registerDemoBatch(DEMO_BATCH_ID, demoBatchStats);
 *   registerDemoTimelineEvents(DEMO_BATCH_ID, demoEvents);
 *
 *   // In reducer (loadSingleBatch, getMachineEventTimelineByBatchId, etc.):
 *   const demo = getDemoBatch(batchId);
 *   if (demo) return { statistics: demo };
 *
 *   // In tour cleanup thunk:
 *   clearDemoRegistry();
 */

let tourActive = false;

const registry = {
  batches: new Map<number, any>(),
  timelineEvents: new Map<number, any[]>(),
  batchSearch: new Map<number, any>(),
  ncFileContent: new Map<string, string>(),
  machineStatsHour: new Map<number, any[]>(),
  machineStatsHourPrev: new Map<number, any[]>(),
  oeeTeep: new Map<number, any>(),
  tools: new Map<number, any[]>(),
  ncFileById: new Map<number, any>(),
  operationsSearch: null as any | null,
};

// --- Tour active flag (blocks real API calls during tour) ---

export const setTourActive = (active: boolean): void => {
  tourActive = active;
};

export const isTourActive = (): boolean => {
  return tourActive;
};

// --- Batch statistics (used by loadSingleBatch) ---

export const registerDemoBatch = (batchId: number, data: any): void => {
  registry.batches.set(batchId, data);
};

export const getDemoBatch = (batchId: number): any | undefined => {
  const result = registry.batches.get(batchId);
  return result;
};

// --- Timeline events (used by getMachineEventTimelineByBatchId) ---

export const registerDemoTimelineEvents = (batchId: number, events: any[]): void => {
  registry.timelineEvents.set(batchId, events);
};

export const getDemoTimelineEvents = (batchId: number): any[] | undefined => {
  return registry.timelineEvents.get(batchId);
};

// --- Batch search results (used by productionBatchesSearch for quantityEvents) ---

export const registerDemoBatchSearch = (batchId: number, data: any): void => {
  registry.batchSearch.set(batchId, data);
};

export const getDemoBatchSearch = (batchId: number): any | undefined => {
  return registry.batchSearch.get(batchId);
};

// --- NC file content (used by getNCFile) ---

export const registerDemoNcFileContent = (hash: string, content: string): void => {
  registry.ncFileContent.set(hash, content);
};

export const getDemoNcFileContent = (hash: string): string | undefined => {
  return registry.ncFileContent.get(hash);
};

// --- Machine stats hour (used by getMachineStatsPerPeriod tour guard) ---

export const registerDemoMachineStatsHour = (machineId: number, data: any[]): void => {
  registry.machineStatsHour.set(machineId, data);
};

export const getDemoMachineStatsHour = (machineId: number): any[] | undefined => {
  return registry.machineStatsHour.get(machineId);
};

// --- Machine stats hour previous period (used by getMachineStatsPerPeriod tour guard) ---

export const registerDemoMachineStatsHourPrev = (machineId: number, data: any[]): void => {
  registry.machineStatsHourPrev.set(machineId, data);
};

export const getDemoMachineStatsHourPrev = (machineId: number): any[] | undefined => {
  return registry.machineStatsHourPrev.get(machineId);
};

// --- OEE/TEEP statistics (used by machineStatisticsOeeTeep) ---

export const registerDemoOeeTeep = (machineId: number, data: any): void => {
  registry.oeeTeep.set(machineId, data);
};

export const getDemoOeeTeep = (machineId: number): any | undefined => {
  return registry.oeeTeep.get(machineId);
};

// --- Tools (used by getTools for clamping tools) ---

export const registerDemoTools = (ncFileId: number, data: any[]): void => {
  registry.tools.set(ncFileId, data);
};

export const getDemoTools = (ncFileId: number): any[] | undefined => {
  return registry.tools.get(ncFileId);
};

// --- NC file by ID (used by getNCFileById for first-ran info) ---

export const registerDemoNCFileById = (ncFileId: number, data: any): void => {
  registry.ncFileById.set(ncFileId, data);
};

export const getDemoNCFileById = (ncFileId: number): any | undefined => {
  return registry.ncFileById.get(ncFileId);
};

// --- Operations search results (used by operationsSearch for scheduled jobs) ---

export const registerDemoOperationsSearch = (data: any): void => {
  registry.operationsSearch = data;
};

export const getDemoOperationsSearch = (): any | undefined => {
  return registry.operationsSearch ?? undefined;
};

// --- Cleanup ---

export const clearDemoRegistry = (): void => {
  registry.batches.clear();
  registry.timelineEvents.clear();
  registry.batchSearch.clear();
  registry.ncFileContent.clear();
  registry.machineStatsHour.clear();
  registry.machineStatsHourPrev.clear();
  registry.oeeTeep.clear();
  registry.tools.clear();
  registry.ncFileById.clear();
  registry.operationsSearch = null;
  tourActive = false;
};

/**
 * Check if any demo data is registered.
 * Useful for quick bail-out checks in reducers.
 */
export const hasDemoData = (): boolean => {
  return (
    registry.batches.size > 0 ||
    registry.timelineEvents.size > 0 ||
    registry.batchSearch.size > 0 ||
    registry.ncFileContent.size > 0 ||
    registry.machineStatsHour.size > 0 ||
    registry.machineStatsHourPrev.size > 0 ||
    registry.oeeTeep.size > 0 ||
    registry.tools.size > 0 ||
    registry.ncFileById.size > 0 ||
    registry.operationsSearch != null
  );
};
